import React, { useState, useEffect } from 'react'
import ReactHtmlParser from 'react-html-parser';
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import ContactDetailsStep_PreOrder from './ContactDetailsStep/ContactDetailsStep';
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideARBlock, displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'

function PreOrder({
  formExtraParams,
  preDefinedVehicleData,
  preDefinedDealerData,
  preDefinedUserLocation,
  initStep,
  containerRef
}) {
  const { source, language, lastStepBottomContents, initStepTopContents, introductionText, formDisclaimer, isPreorderModal, dealerId } = formExtraParams
  const maxStepNum = 3;

  const isProrderModalFormatted = (isPreorderModal ? isPreorderModal.toLowerCase() === 'true' : false);

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(initStep)
  const [data, setData] = useState({
    vehicleData: preDefinedVehicleData,
    dealerData: preDefinedDealerData,
    contactDetails: null
  })
	const [returnToStart, setReturnToStart] = useState(false);

	const step1Title = !returnToStart ? '' : languageController.getCopy('findDealer', 'reserveYours');

  // Display top contents on the init step.
  useEffect(() => {
    if (initStep === 0 || initStep > 0) {
      displayOutsideComponents(initStepTopContents)
    }
    // Add the form-related vars to DTM dataLayer.
    pushToGTMDataLayer({ form: 'Pre Order' });
    pushToGTMDataLayer({ formid: 'PreOrder' });
    // If initStep is later than 0, handle analytics code for each step.
    if (initStep > 0) {
      for (let step = 0; step < initStep; step++) {
        setupTrackingCodes(data, step);
      }
    }else{
    	setReturnToStart(true);
	}
  }, [])

  useEffect(() => {
    // Update 'formstep' dataLayer var when currentStep is changed
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
    // Handle mz-page-top padding based on current step.
    const containerEl = containerRef.current;
    if (containerEl) {
       if (currentStep === 0 || (initStep >0 && !returnToStart)) {
        containerEl.classList.add('mz-page-top__disabled');
      } else {
        containerEl.classList.remove('mz-page-top__disabled');
      }
	  if(initStep >0 && !returnToStart && currentStep > 0){
		  containerEl.querySelector('.mz-pre-order__steps').classList.remove('add_margin');
	  }else{
		  containerEl.querySelector('.mz-pre-order__steps').classList.add('add_margin');
	  }
    }
  }, [currentStep])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === 1) {
      // Hide GaQ Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display GaQ related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
      // Display AR Block of the selected model
      displayOutsideARBlock(data.vehicleData?.model)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    // Scroll to the top of the screen.
    scrollToTop();
  }

  // Handle adding analytics tracking attributes.
  const setupTrackingCodes = async (data, completedStep) => {
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 0 && data.vehicleData) {
        const trackData = { modelCarlineName : data.vehicleData.model, modelYear : data.vehicleData.modelYear, msrp : data.vehicleData.msrp };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 1 && data.dealerData) {
        const trackData = { dealerName : data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 2 && data.contactDetails) {
        const trackData = { email : data.contactDetails.email, firstname: data.contactDetails.firstname, lastname : data.contactDetails.lastname, postalcode : data.contactDetails.postalcode, phone : data.contactDetails.phone, optin : data.contactDetails.optin === 'on' ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'Contact Details Submit');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div
      className="mz-pre-order"
      data-current-step={currentStep}
    >
      {!window.bnp_retailer_mode &&
        <FormStepsNav
          isActive={(currentStep > 0 && currentStep < maxStepNum)}
          currentStep={currentStep}
          currentProgress={currentProgress}
          refreshText={languageController.getCopy('global', 'vehicles')}
          setCurrentStep={setCurrentStep}
          navList={[
            {
              step: 1,
              label: languageController.getCopy('global', 'selectDealerNav'),
            },
            {
              step: 2,
              label: languageController.getCopy('global', 'contactDetailsNav'),
            },
          ]}
        />
      }
      <div className="mz-pre-order__steps">
        {/* Step 0: Select a vehicle */}
        <StepWrapper
          className={`mz-pre-order__select-vehicle`}
          stepNum={0}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectVehicleStep
            title={languageController.getCopy('global', 'selectVehicle')}
            handleNextStep={handleNextStep}
            introductionText={introductionText}
          />
        </StepWrapper>
        {/* Step 1: Select a dealer */}
        <StepWrapper
          className={`mz-pre-order__select-dealer`}
          type="select-dealer"
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            vehicleData={data.vehicleData}
            title={step1Title}
            isPreorderModal={isProrderModalFormatted}
            subTitle={languageController.getCopy('global', 'findDealerSub')}
            preDefinedUserLocation={preDefinedUserLocation}
            preDefinedDealerData={data.dealerData?.dealer}
            currentStep={currentStep}
			      startingAt={languageController.getCopy('global', 'pricePrefix')}
            displayPrice={false}
          />
        </StepWrapper>
        {/* Step 2: Enter Contact Details */}
        <StepWrapper
          className={`mz-pre-order__contact-details`}
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ContactDetailsStep_PreOrder
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'fillContactForm')}
            submitButtonText={languageController.getCopy('preOrder', 'step2Submit')}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
            vehicleData={data.vehicleData}
            formSource={source}
            formPid={pidController.getPid('preOrder')}
            formEndPointUrl={getEndpointUrl('pre-order')}
            formLanguage={convertLangCodeToSingleLetter(language)}
            formDisclaimer={formDisclaimer}
            isPreorderModal={isProrderModalFormatted}
          />
        </StepWrapper>
        {/* Step 3: Show Confirmation */}
        <StepWrapper
          className={`mz-pre-order__confirmation`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ConfirmationStep
            vehicleData={data.vehicleData}
            dealerData={data.dealerData}
            contactDetails={data.contactDetails}
            displayPrice={false}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

PreOrder.propTypes = {
  formExtraParams: PropTypes.object.isRequired,
  preDefinedVehicleData: PropTypes.object,
  preDefinedDealerData: PropTypes.object,
  preDefinedUserLocation: PropTypes.object,
  initStep: PropTypes.number.isRequired
}

PreOrder.defaultProps = {
  formExtraParams: {},
  preDefinedVehicleData: null,
  preDefinedDealerData: null,
  preDefinedUserLocation: null,
  initStep: 0
}

export default PreOrder